import React, {useState, useEffect, useRef} from "react";
import Pagination from "../../components/Pagination";
import Select2 from "../../components/Select2";
import {errorStatusHandle} from "../../utils/error-utils";
import {getSelectLabel, getRoleUserOption, getStatusUserOption} from "../../utils/select-utils";
import {searchUsers} from "../../services/user-master/SearchScreen";
import { ROLE_USER, STATUS_USER} from "../../constants/app";
import {useDocumentTitle, useFileDownloader} from "../../hooks";
import UserMasterLayout from "../../layouts/UserMasterLayout";
import {useDispatch} from 'react-redux';
import {setPageSettings} from "../../store/actions/pageActions";
import {getUserSearchPageSettings} from "../../api/setting/userMasterPageApi";
import getPageBreadcrumb from "../../services/BreadcrumbServices";
import {USER_MASTER_SEARCH} from "../../constants/routes";
import {PageTitle} from "../../components/admin";
import Table from "../../components/table/Table";
import {API_USER_MASTER_DOWNLOAD_SEARCH_RESULT} from "../../constants/api";
import { USER_SETTING_CATEGORIES } from "../../constants/app";
import {getUserSetting, updateUserSetting} from "../../services/user-master/UserSettingServices"
import {
  TextInput,
  Label,
  FormGroup,
  FormGroupInput,
  ErrorMessageAlert,
  SelectInput,
  Button,
  ButtonFilterSearch,
  ButtonDPad,
  RedirectAlert
} from "../../components";
import {
  clearPageLoading,
  setPageLoading
} from '../../store/actions';
import {
  BUTTON_EXPORT_USER_SEARCH,
  BUTTON_SEARCH_USER
} from "../../constants/buttons";
import {getInputErrorMessage, hasErrorInput} from "../../utils";
import {setButtonProcessing, clearButtonProcessing} from "../../store/actions";
import tenkenTheme from "../../configs/themes/tenkenTheme";
import { FiDownloadCloud } from "react-icons/fi";
import {USER_MASTER_SEARCH_PAGE} from "../../constants/pageName";
import useRedirectPage from "../../hooks/useRedirectPage";

function UserSearchPage() {
  useRedirectPage(USER_MASTER_SEARCH_PAGE);
  const {downloadFile, errorMessage} = useFileDownloader();
  const [tableData, setTableData] = useState([]);
  const [formError, setFormError] = useState([]);
  const [totalPage, setTotalPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const isFirstRun = useRef(true);
  const [sortOrderList, setSortOrderList] = useState('');
  const [userSettingData, setUserSettingData] = useState({
    role: ROLE_USER.ALL,
    status: STATUS_USER.ALL
  });
  const [formSearchUserData, setFormSearchUserData] = useState({
    username: '',
    name: '',
    email: '',
    role: ROLE_USER.ALL,
    status: STATUS_USER.ALL,
    page: 1,
    per_page: 10,
    sort: ''
  });

  const selectOptionsRole = getRoleUserOption(true);
  const selectOptionsStatus = getStatusUserOption(true);
  const dispatch = useDispatch();

  const searchUser = async () => {
    try {
      const response = await searchUsers(formSearchUserData);
      setTableData(response.data.users);
      setFormError([]);
      setTotalPage(response.data.pagination.total_pages);
      setTotalCount(response.data.pagination.total_count);
      dispatch(clearPageLoading());
    } catch (e) {
      dispatch(clearPageLoading());
      errorStatusHandle(e);
      if (e.status === 400) {
        setFormError(e.data)
      }
    }
  };

  const getDataSearchUserSettings = async () => {
    dispatch(setPageLoading());
    try {
      const userSettings = await getUserSetting(USER_SETTING_CATEGORIES.USER_SEARCH);
      const formInputOptions = userSettings?.data?.setting ?? null;
      let dataSearch = formSearchUserData;
      if (formInputOptions) {
        setUserSettingData(formInputOptions);
        setFormSearchUserData(prevState => ({
          ...prevState,
          ...formInputOptions
        }));
        dataSearch = {...formSearchUserData, ...formInputOptions}
      }
      const response = await searchUsers(dataSearch);
      setTableData(response.data.users);
      setFormError([]);
      setTotalPage(response.data.pagination.total_pages);
      setTotalCount(response.data.pagination.total_count);
    } catch (e) {
      errorStatusHandle(e)
    } finally {
      dispatch(clearPageLoading());
    }
  };

  const updateSettings = async () => {
    try {
      await updateUserSetting(userSettingData, USER_SETTING_CATEGORIES.USER_SEARCH);
    } catch (e) {
      errorStatusHandle(e);
      if (e.status === 400) {
        setFormError(e.data)
      }
    }
  };

  useEffect(() => {
    if (isFirstRun.current) {
      return;
    }
    searchUser()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formSearchUserData.page, formSearchUserData.per_page]);

  useEffect(() => {
    if (isFirstRun.current) {
      return;
    }
    if (formSearchUserData.sort) {
      const parts = formSearchUserData.sort.split(',').reduce((acc, cur) => {
        const [column, order] = cur.split('_');
        acc[column] = order;
        return acc;
      }, {});
      setSortOrderList(parts)
    } else {
      setSortOrderList([])
    }
    searchUser();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formSearchUserData.sort]);

  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
    }
    const response = getUserSearchPageSettings();
    dispatch(setPageLoading());
    dispatch(setPageSettings(response.data));
    getDataSearchUserSettings();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePageChange = (page) => {
    setFormSearchUserData(prev => ({...prev, page}));
  };

  const handleSearch = () => {
    updateSettings();
    dispatch(setButtonProcessing(BUTTON_SEARCH_USER));
    searchUser().finally(() => {
      dispatch(clearButtonProcessing(BUTTON_SEARCH_USER))
    })
  };

  const handlePageSizeChange = (per_page) => {
    setFormSearchUserData(prev => ({...prev, per_page}));
  };

  const handleSortColumn = (columnName) => {
    const currentSorts = formSearchUserData.sort ? formSearchUserData.sort.split(',') : [];
    const currentColumnSort = currentSorts.find(sort => sort.startsWith(columnName));
    const newSorts = currentSorts.filter(sort => !sort.startsWith(columnName));
    if (!currentColumnSort) {
      newSorts.push(`${columnName}_asc`);
    } else if (currentColumnSort.endsWith('_asc')) {
      newSorts.push(`${columnName}_desc`);
    }
    setFormSearchUserData(prev => ({...prev, sort: newSorts.join(',')}));
  };

  const handleSelectInputChange = (selectOption, name) => {
    var value = '';
    if (selectOption) {
      value = selectOption.value
    }

    setFormSearchUserData({
      ...formSearchUserData,
      [name]: value
    });

    setUserSettingData({
      ...userSettingData,
      [name]: value
    })
  };

  const handleInputChange = (e) => {
    setFormSearchUserData({
      ...formSearchUserData,
      [e.target.name]: e.target.value
    });
  };

  const handleSelectChange = (e, name) => {
    setFormSearchUserData({
      ...formSearchUserData,
      [name]: e.target.value
    });

    setUserSettingData({
      ...userSettingData,
      [name]: e.target.value
    })
  };

  const handleDownload = () => {
    dispatch(setButtonProcessing(BUTTON_EXPORT_USER_SEARCH));
    downloadFile(API_USER_MASTER_DOWNLOAD_SEARCH_RESULT, formSearchUserData).finally(() => {
      dispatch(clearButtonProcessing(BUTTON_EXPORT_USER_SEARCH));
    });
  };

  const getDefaultOption = (selectOption, value) => {
    if (value) {
      return selectOption.find((option) => option.value === value);
    }
    return '';
  };

  const breadcrumb = getPageBreadcrumb(USER_MASTER_SEARCH);
  useDocumentTitle("ユーザー検索 - ユーザーマスタ");

  return (
    <UserMasterLayout breadcrumb={breadcrumb}>
      <PageTitle title="ユーザー検索"/>
      <ButtonDPad />
      <div className="relative max-w-[1460px] xl:min-w-[1140px] lg:min-w-[1000px] min-w-[950px] mr-[24px] inline-block">
        <RedirectAlert />
        <ErrorMessageAlert className="mb-[16px]" error={errorMessage}/>
        <form
          className="bg-white rounded-lg w-full border border-[#e0e0e0] px-[20px] pt-[12px] pb-[4px] mt-[10px] flex 2xl:space-x-[38px] xl:space-x-[22px] space-x-[16px]">
          <FormGroup direction="vertical">
            <Label text="ユーザーID" required={false} htmlFor="username" className="mb-[4px]"/>
            <FormGroupInput error={getInputErrorMessage(formError.errors, "username")}>
              <TextInput
                className="2xl:max-w-[160px] 2xl:w-full xl:w-[150px] w-[130px] xl:text-base text-[15px]  text-tenken-tablet-regular"
                name="username" autoFocus
                id="username" maxLength={20} value={formSearchUserData.username} onChange={handleInputChange}
                hasError={hasErrorInput(formError.errors, "username")}/>
            </FormGroupInput>
          </FormGroup>
          <FormGroup direction="vertical">
            <Label text="ユーザー名" required={false} htmlFor="name" className="mb-[4px]"/>
            <FormGroupInput error={getInputErrorMessage(formError.errors, "name")}>
              <TextInput
                className="2xl:max-w-[240px] 2xl:w-full xl:w-[160px] w-[150px] xl:text-base text-[15px] text-tenken-tablet-regular"
                name="name"
                id="name" maxLength={40} value={formSearchUserData.name} onChange={handleInputChange}
                hasError={hasErrorInput(formError.errors, "name")}/>
            </FormGroupInput>
          </FormGroup>
          <FormGroup direction="vertical">
            <Label text="メールアドレス" required={false} htmlFor="email" className="mb-[4px]"/>
            <FormGroupInput error={getInputErrorMessage(formError.errors, "email")}>
              <TextInput
                className="2xl:w-[380px] xl:w-[220px] w-[180px] xl:text-base text-[15px]  text-tenken-tablet-regular"
                name="email"
                id="name" maxLength={200} value={formSearchUserData.email} onChange={handleInputChange}
                hasError={hasErrorInput(formError.errors, "email")}/>
            </FormGroupInput>
          </FormGroup>
          <FormGroup direction="vertical">
            <Label text="ロール" required={false} htmlFor="role" className="mb-[4px]"/>
            <FormGroupInput error={getInputErrorMessage(formError.errors, "role")}>
              <Select2
                options={selectOptionsRole}
                className='xl:text-base text-[15px]  text-tenken-tablet-regular xl:w-[210px] lg:w-[190px] w-[140px]'
                classNamePrefix="react-select"
                nextFocus='status'
                value={getDefaultOption(selectOptionsRole, formSearchUserData.role)}
                onChange={(selectOptionsRole) => handleSelectInputChange(selectOptionsRole, 'role')}
                name="role"
                hasError={hasErrorInput(formError.errors, "role")}
                maxLength={15}
              />
            </FormGroupInput>
          </FormGroup>
          <FormGroup direction="vertical">
            <Label text="状態" required={false} htmlFor="status" className="mb-[4px]"/>
            <FormGroupInput error={getInputErrorMessage(formError.errors, "status")}>
              <SelectInput
                options={selectOptionsStatus}
                className='xl:text-base text-[15px]  text-tenken-tablet-regular 2xl:w-[120px] xl:w-[100px] w-[90px]'
                value={formSearchUserData.status}
                onChange={(e) => handleSelectChange(e, 'status')}
                hasError={hasErrorInput(formError.errors, "status")}
                name="status"
                id="status"
              />
            </FormGroupInput>
          </FormGroup>
          <div className="pt-[33px]">
            <ButtonFilterSearch className="xl:text-base text-[15px]"  id={BUTTON_SEARCH_USER} onClick={() => handleSearch()} />
          </div>
        </form>
        <div className="relative mt-[24px] mb-[12px]">
          <div className="rounded-lg border border-[#e0e0e0]">
            <Table className="no-drop-shadow-table" striped>
              <Table.Head className="whitespace-nowrap">
                <Table.HeadCell
                  className="w-[80px]"
                >
                  <span>#</span>
                </Table.HeadCell>
                <Table.HeadCell
                  className="w-[200px]"
                  sortable
                  order={sortOrderList.username}
                  onChangeOrder={() => handleSortColumn("username")}
                >
                  <span>ユーザーID</span>
                </Table.HeadCell>
                <Table.HeadCell
                  className="w-[250px]"
                  sortable
                  order={sortOrderList.name}
                  onChangeOrder={() => handleSortColumn("name")}
                >
                  <span>ユーザー名</span>
                </Table.HeadCell>
                <Table.HeadCell
                  className="w-[470px]"
                  sortable
                  order={sortOrderList.email}
                  onChangeOrder={() => handleSortColumn("email")}
                >
                  <span>メールアドレス</span>
                </Table.HeadCell>
                <Table.HeadCell
                  className="w-[200px]"
                  sortable
                  order={sortOrderList.role}
                  onChangeOrder={() => handleSortColumn("role")}
                >
                  <span>ロール</span>
                </Table.HeadCell>
                <Table.HeadCell
                  className="w-[140px]"
                  sortable
                  order={sortOrderList.status}
                  onChangeOrder={() => handleSortColumn("status")}
                >
                  <span>状態</span>
                </Table.HeadCell>
              </Table.Head>
              <Table.Body className="devide-y xl:text-base text-[15px]">
                {tableData.map((row, index) => (
                    <Table.Row
                      key={index}>
                      <Table.Cell>
                        <span>{(formSearchUserData.page - 1)  * formSearchUserData.per_page + (index + 1)}</span>
                      </Table.Cell>
                      <Table.Cell>
                        <span>{row.username}</span>
                      </Table.Cell>
                      <Table.Cell>
                        <span className="max-w-[200px] xl:max-w-[250px] truncate inline-block align-middle" title={row.name}>{row.name}</span>
                      </Table.Cell>
                      <Table.Cell>
                        <span className="max-w-[360px] xl:max-w-[470px] truncate inline-block align-middle" title={row.email}>{row.email}</span>
                      </Table.Cell>
                      <Table.Cell className="whitespace-nowrap">
                        <span>{getSelectLabel(selectOptionsRole, row.role)}</span>
                      </Table.Cell>
                      <Table.Cell className="user-search-table-sisuation">
                        <div
                          className={`w-[68px] h-[24px] text-[13px] leading-[24px] flex items-center justify-center text-white text-center rounded-[12px] ${
                            row.status === STATUS_USER.ACTIVE ? "bg-[#3BAE4D]" : "bg-[#FA5252]"
                            }`}
                        >
                           {getSelectLabel(selectOptionsStatus, row.status)}
                        </div>
                      </Table.Cell>
                    </Table.Row>
                  )
                )}
              </Table.Body>
            </Table>
          </div>
        </div>
        {tableData.length > 0 ? (
          <div className="flex space-x-[24px]">
            <Button
              theme={tenkenTheme.button}
              color="success"
              type="button"
              onClick={() => handleDownload()}
              id={BUTTON_EXPORT_USER_SEARCH}
              className="min-w-[180px] rounded-[6px] h-[40px] text-white border-[#1DBB36] flex items-center justify-center hover:bg-[#2F8C3B]"
            >
              <FiDownloadCloud />
              <span className="ml-1 whitespace-nowrap xl:text-base text-[15px] ">ダウンロード</span>
            </Button>
            <div className="w-full">
              <Pagination
                showIndex={totalCount > 0}
                setPage={handlePageChange}
                currentPage={formSearchUserData.page}
                setPageSize={totalCount > 0 ? handlePageSizeChange : null}
                pageSize={formSearchUserData.per_page}
                numPages={totalPage}
                totalCount={totalCount}/>
            </div>
          </div>
        ) : (
          <p className="xl:text-base text-[15px]">該当する検索結果が見つかりませんでした。別の検索条件をお試しください。</p>
        )}
      </div>
    </UserMasterLayout>
  );
}

export default UserSearchPage;

